import { useGate } from 'statsig-react';
import {
  type ChannelCuratorsQuery,
  type ChannelSettingsQuery,
  MediaType,
} from '@graphql/reactQuery';
import { FEATURE_GATES } from '@utils/constants';
import type { FieldsInput } from './settings/store/fields';

type Channel = {
  channel: NonNullable<ChannelSettingsQuery['channelBySlug']>;
  channelCurators: NonNullable<ChannelCuratorsQuery['channelCurators']['edges'][number]['node']>[];
};

export function getFields({ channel, channelCurators }: Channel): FieldsInput {
  return {
    name: channel.name,
    slug: channel.nameSlug,
    description: channel.description,
    coverImage: channel.coverImage?.id
      ? {
          cdnUrl: channel.coverImage.url,
          mediaType: MediaType.ChannelCoverImage,
          uploadKey: channel.coverImage.key,
        }
      : null,
    profileImage: channel.profileImage?.id
      ? {
          cdnUrl: channel.profileImage.url,
          mediaType: MediaType.ChannelProfileImage,
          uploadKey: channel.profileImage.key,
        }
      : null,
    moderators: channelCurators
      .filter(node => node.role === 'MODERATOR')
      .flatMap(({ user, role }) => {
        return {
          id: user.id,
          role,
          avatarUri: user.avatar?.url,
          publicAddress: user.publicAddress,
          webappUri: user.artist ? user.artist.webappUri : user.webappUri,
          displayName: user.artist ? user.artist.name : user.displayName,
          isArtist: !!user.artist,
        };
      }),
    // Links
    telegramUrl: channel.telegramUrl,
    tiktokUrl: channel.tiktokUrl,
    twitterUrl: channel.twitterUrl,
    websiteUrl: channel.websiteUrl,
    discordUrl: channel.discordUrl,
  };
}

export function useChannelSettingsPath(channelSlug: string) {
  return `/c/${channelSlug}/settings`;
}

export function useChannelsEnabled() {
  const { value: enabled, isLoading } = useGate(FEATURE_GATES.CHANNELS);

  return { enabled, isLoading };
}
